import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Divider,
  Grid,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  HowToVote as VoteIcon,
  Assessment as ResultsIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';

// API hooks
import { 
  useGetPresentationByIdQuery, 
  useUpdatePresentationMutation 
} from '../../services/api';

// Redux
import { useAppSelector } from '../../hooks/redux';
import { Question } from '../../services/slices/presentationSlice';

const PresentationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.attributes?.['custom:role'] === 'admin';
  
  // State
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<'activate' | 'deactivate' | 'delete'>('activate');
  
  // Fetch presentation
  const { data: presentation, isLoading, error } = useGetPresentationByIdQuery(id || '');
  
  // Mutations
  const [updatePresentation] = useUpdatePresentationMutation();
  
  if (isLoading) {
    return <Typography>Loading presentation...</Typography>;
  }
  
  if (error || !presentation) {
    return <Typography color="error">Error loading presentation</Typography>;
  }
  
  const handleActivatePresentation = async () => {
    try {
      await updatePresentation({
        id: presentation.id,
        status: 'active',
      }).unwrap();
      setConfirmDialogOpen(false);
    } catch (error) {
      console.error('Failed to activate presentation:', error);
    }
  };
  
  const handleDeactivatePresentation = async () => {
    try {
      await updatePresentation({
        id: presentation.id,
        status: 'completed',
      }).unwrap();
      setConfirmDialogOpen(false);
    } catch (error) {
      console.error('Failed to deactivate presentation:', error);
    }
  };
  
  const openConfirmDialog = (action: 'activate' | 'deactivate' | 'delete') => {
    setDialogAction(action);
    setConfirmDialogOpen(true);
  };
  
  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'info';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/presentations')}
          sx={{ mb: 2 }}
        >
          Back to Presentations
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {presentation.title}
            </Typography>
            <Chip
              label={presentation.status.charAt(0).toUpperCase() + presentation.status.slice(1)}
              color={getStatusChipColor(presentation.status) as any}
              sx={{ mb: 2 }}
            />
          </Box>
          
          {isAdmin && (
            <Box>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/presentations/${presentation.id}/edit`)}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              
              {presentation.status === 'active' ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={() => openConfirmDialog('deactivate')}
                >
                  End Voting
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<StartIcon />}
                  onClick={() => openConfirmDialog('activate')}
                  disabled={presentation.status === 'completed'}
                >
                  Start Voting
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body1" paragraph>
                {presentation.description}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Presenter</Typography>
                  <Typography variant="body1">{presentation.presenter}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Date</Typography>
                  <Typography variant="body1">
                    {new Date(presentation.date).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Created</Typography>
                  <Typography variant="body1">
                    {new Date(presentation.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Last Updated</Typography>
                  <Typography variant="body1">
                    {new Date(presentation.updatedAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                {presentation.status === 'active' && (
                  <Button
                    variant="contained"
                    startIcon={<VoteIcon />}
                    onClick={() => navigate(`/vote/${presentation.id}`)}
                    sx={{ mr: 2 }}
                  >
                    Vote Now
                  </Button>
                )}
                
                {isAdmin && (
                  <Button
                    variant="outlined"
                    startIcon={<ResultsIcon />}
                    onClick={() => navigate(`/results/${presentation.id}`)}
                  >
                    View Results
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Questions ({presentation.questions.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {presentation.questions.length > 0 ? (
                <List>
                  {[...presentation.questions]
                    .sort((a: Question, b: Question) => a.order - b.order)
                    .map((question: Question, index: number) => (
                      <Paper
                        key={question.id}
                        elevation={1}
                        sx={{ mb: 2, p: 2 }}
                      >
                        <Typography variant="subtitle1" gutterBottom>
                          {index + 1}. {question.text}
                        </Typography>
                        
                        {question.options.length > 0 && (
                          <List dense disablePadding>
                            {question.options.map((option: string, optIndex: number) => (
                              <ListItem key={optIndex} disablePadding sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={`${String.fromCharCode(65 + optIndex)}. ${option}`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Paper>
                    ))}
                </List>
              ) : (
                <Typography>No questions added yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>
          {dialogAction === 'activate'
            ? 'Start Voting Session'
            : dialogAction === 'deactivate'
            ? 'End Voting Session'
            : 'Delete Presentation'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogAction === 'activate'
              ? 'Are you sure you want to start the voting session for this presentation? This will make it active and allow users to vote.'
              : dialogAction === 'deactivate'
              ? 'Are you sure you want to end the voting session? This will mark the presentation as completed and prevent further voting.'
              : 'Are you sure you want to delete this presentation? This action cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={
              dialogAction === 'activate'
                ? handleActivatePresentation
                : dialogAction === 'deactivate'
                ? handleDeactivatePresentation
                : () => {} // Handle delete
            }
            color={dialogAction === 'delete' ? 'error' : 'primary'}
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PresentationDetail;
